/**
 * Явно размеченные сущности (07-оболочка-тз, «Явно размеченные сущности»).
 *
 * Подсвечивается только то, что автор разметил руками. Совпадение текста
 * с названием сущности не значит ничего: «дело» в предложении — обычное слово,
 * а не карточка, и оболочка не должна догадываться, где именно автор имел в виду
 * термин. Догадки читаются игроком как обещание — подсвеченное он пробует
 * набрать, — и каждая ложная подсветка стоит доверия ко всем остальным.
 *
 * Синтаксис — ссылка Obsidian: `[[ref-ines]]` или `[[ref-ines|шкалу событий]]`,
 * где после черты стоит форма, напечатанная в предложении. Выбрана она не от
 * бедности: автор пишет в Obsidian, ссылка там кликается и подсвечивается сама,
 * а несуществующая видна как битая ещё до того, как о ней скажет валидатор.
 * Отдельного синтаксиса ради отдельного синтаксиса заводить незачем.
 *
 * Тип сущности не пишется: он **следует из id**. Слово «Дела» и термин
 * справочника живут в разных реестрах, и одно и то же имя в обоих — уже ошибка
 * контента, а не повод писать префикс в каждом упоминании.
 */

import type { GameContent, SaveState } from './types.ts';

/**
 * Размечают только знание — то, что Марго знает и может назвать. Предметы
 * в тексте не размечаются: они доступны как вещи комнаты или её собственность,
 * а не как справочная ссылка, и ссылка на вещь обещала бы игроку не то.
 */
export type EntityKind = 'reference' | 'word';

export interface EntityMention {
  kind: EntityKind;
  id: string;
  /** Форма, напечатанная в предложении: склонённая и не обязана совпадать с названием. */
  label: string;
  /**
   * Которое это по счёту вхождение такой же формы в этом блоке текста.
   *
   * Нужно затем, что подсветка ищет форму в строке по тексту, а слово может
   * повториться: размечено первое «учебник», а подсветить второе — ровно та
   * самая догадка, от которой этот файл и заводился.
   */
  nth: number;
}

/** `[[id]]` или `[[id|форма в предложении]]`. */
const LINK = /\[\[([^\]|]+?)(?:\|([^\]]+?))?\]\]/g;

/**
 * Снять разметку с текста: вернуть то, что увидит игрок, и упоминания по порядку.
 *
 * Разрешение и доступность сюда не входят — это чистый разбор, и он одинаково
 * нужен и оболочке, и валидатору, у которого нет ни сейва, ни позиции игрока.
 */
export function parseEntities(raw: string): { text: string; mentions: Omit<EntityMention, 'kind'>[] } {
  const mentions: Omit<EntityMention, 'kind'>[] = [];
  const seen = new Map<string, number>();

  const text = raw.replace(LINK, (_, id: string, alias: string | undefined) => {
    const label = (alias ?? id).trim();
    const nth = seen.get(label) ?? 0;
    seen.set(label, nth + 1);
    mentions.push({ id: id.trim(), label, nth });
    return label;
  });

  return { text, mentions };
}

/**
 * Текст без разметки — то, что видит игрок.
 *
 * Нужен везде, где текст заметки показывается **не потоком**: реплика
 * в предпросмотре, карточка слова, статья справочника, вступление предмета.
 * Поток снимает разметку сам, когда строит запись, а эти поверхности берут
 * текст напрямую — и без этой функции показывают игроку `[[ref-ines|шкалу]]`
 * вместо «шкалу».
 */
export function plainText(raw: string): string {
  return parseEntities(raw).text;
}

/**
 * Чем оказался id. `null` — ничем: такую ссылку валидатор считает ошибкой,
 * а оболочка печатает форму обычным текстом, не выдумывая ей смысла.
 */
export function kindOf(content: GameContent, id: string): EntityKind | null {
  if (content.words[id]) return 'word';
  if (content.reference[id]) return 'reference';
  return null;
}

/**
 * Доступна ли подсветка (07-оболочка-тз, «Явно размеченные сущности»).
 *
 * Правила разные, и различие содержательное. Термин справочника объясняет мир
 * и доступен всегда. Слово «Дела» — инструмент, и подсветить его раньше, чем
 * игрок его получил, значит показать команду, которой у него нет; само
 * упоминание карточку при этом не выдаёт.
 */
export function available(kind: EntityKind, id: string, save: SaveState): boolean {
  return kind === 'reference' || save.words[id] != null;
}

/** Класс подсветки: он же определяет цвет. Термин и слово различаются в палитре. */
export function entityClass(kind: EntityKind): string {
  return kind === 'word' ? 'word' : 'term';
}

/**
 * Разметка → упоминания, которые игрок действительно увидит подсвеченными.
 *
 * Недоступное упоминание не исчезает из текста и не становится ошибкой: форма
 * печатается как обычная проза. Это и есть смысл правила «упоминание не даёт
 * доступ» — про слово, которого у Марго ещё нет, можно написать, не обещая
 * игроку команду.
 */
export function resolveEntities(
  content: GameContent,
  save: SaveState,
  raw: string,
): { text: string; mentions: EntityMention[] } {
  const parsed = parseEntities(raw);
  const mentions: EntityMention[] = [];
  for (const mention of parsed.mentions) {
    const kind = kindOf(content, mention.id);
    if (kind && available(kind, mention.id, save)) mentions.push({ ...mention, kind });
  }
  return { text: parsed.text, mentions };
}
